// profile-detail.ts — the shared player-profile detail modal, rendered by BOTH the in-game
// LEADERBOARD screen's player-detail popup (leaderboard-view.ts) and the standalone MY PROFILE
// screen (profile-view.ts). Pure render: a 184u modal box with the player's lifetime stats + a
// RECENT GAMES list (each row carries a PLAY button when a replay recording exists). The data +
// fetch live in the controllers; the OWNING view supplies its own 9-slice button factory (the
// composite cache is per-view, so it can't be shared as a module singleton).

import type { LeaderProfile } from './leaderboard-controller.ts';
import {
  COL_FG,
  COL_ACCENT,
  TEXT_SHADOW,
  FONT_BODY,
  el,
  formScale,
  at,
  buttonWidthU,
  isPortraitBoost,
} from './chrome.ts';

const FONT = FONT_BODY;
const shadow = TEXT_SHADOW;

/** The owning view's shell-button factory (chrome.ts makeShellButton) — the SAME signature the
 *  menu / lobby / leaderboard views use. Passed in so the modal reuses each view's composite cache. */
export type ShellBtn = (
  label: string,
  onClick: () => void,
  enabled?: boolean,
  size?: { w: number; h: number },
) => HTMLButtonElement;

/** loading → fetch in flight · ready → profile shown · empty → no profile yet (own profile, no MP
 *  games) · error → fetch failed. 'empty' is the friendly own-profile state the MY PROFILE screen
 *  shows; the leaderboard only ever passes loading/ready/error. */
export type ProfileDetailStatus = 'loading' | 'ready' | 'empty' | 'error';

export interface ProfileModalDeps {
  /** the title-bar text (the player name, or a 'PLAYER' fallback while loading). */
  title: string;
  status: ProfileDetailStatus;
  profile: LeaderProfile | null;
  /** the owning view's button factory (per-view composite cache). */
  btn: ShellBtn;
  /** CLOSE button + click-outside the box. */
  onClose: () => void;
  /** when set (the MY PROFILE screen — never the leaderboard), a SIGN OUT button in the header bar. */
  onSignOut?: () => void;
  /** open the in-browser replay viewer for one recent game (controller.watchReplay). */
  onWatch: (replayKey: string) => void;
  /** the 'empty' message (e.g. "Play a multiplayer game to start your profile."). */
  emptyText?: string;
  /** the 'error' message — defaults to the leaderboard's "Could not load this profile." */
  errorText?: string;
}

const RECENT_MAX = 5;

/** Build the player-detail modal overlay (a dimmed backdrop + the 184u box). Returns the overlay
 *  element to append to the screen root; the caller owns when it's shown (controller.detailName). */
export function profileModal(d: ProfileModalDeps): HTMLElement {
  const ready = d.status === 'ready' && d.profile !== null;
  const recent = ready ? (d.profile!.recentGames ?? []).slice(0, RECENT_MAX) : [];
  // Box height = the last content row + a bottom margin. Closing is the header × (no bottom button);
  // the recent-games rows (≥1 line for the "no games" note) + the one-line "unranked games" caption
  // (when any games are listed) drive the height. Loading / empty / error stay compact.
  const captionH = ready && recent.length > 0 ? 12 : 0;
  const bodyH = ready ? 132 + Math.max(1, recent.length) * 12 + captionH + 10 : 88;
  // The box must fit the phone-portrait viewport: 160u (= PORTRAIT_FIT_W, the width the boost is
  // calibrated to) in portrait, the roomier 184u on desktop / landscape. Content (incl. the
  // right-anchored ▶ button) lays out relative to boxW so it stays on-screen at the boosted scale.
  const boxW = isPortraitBoost() ? 160 : 184;
  const cw = boxW - 20; // content width (a 10u inset on each side)
  const { overlay, box, s } = modalBox(d.title, boxW, bodyH, d.onClose, d.onSignOut);

  if (d.status === 'loading') {
    box.append(at(centerText('Loading profile…', s), s, 10, 64, cw));
  } else if (d.status === 'empty') {
    box.append(at(centerText(d.emptyText ?? 'No profile yet.', s), s, 10, 56, cw));
  } else if (!ready) {
    box.append(at(centerText(d.errorText ?? 'Could not load this profile.', s), s, 10, 64, cw));
  } else {
    const p = d.profile!;
    const cb = p.combat;
    box.append(at(label(p.name, s, COL_ACCENT), s, 10, 16, cw));
    const lines: Array<[string, string]> = [
      ['Rating', String(Math.round(p.rating))],
      ['Record', `${p.wins}-${p.losses}  (${Math.round(p.winRate * 100)}% win)`],
      ['Units (killed / lost)', `${cb.unitsKilled} / ${cb.unitsLost}  (K/D ${cb.kd.toFixed(1)})`],
      ['Structures (killed / lost)', `${cb.structsKilled} / ${cb.structsLost}`],
      ['Credits acquired', cb.creditsAcquired.toLocaleString()],
      ['Playtime', fmtHours(cb.playtimeSecs)],
      ['Member since', fmtDate(p.createdUtc)],
      ['Last seen', fmtDate(p.lastGameUtc)],
    ];
    let y = 28;
    for (const [k, v] of lines) {
      box.append(at(detailLine(k, v, s), s, 10, y, cw));
      y += 11;
    }

    box.append(at(label('RECENT GAMES', s, COL_ACCENT), s, 10, 120, cw));
    let ry = 132;
    if (recent.length === 0) {
      box.append(at(rowText('No recent games.', s, COL_FG, '0.6'), s, 10, ry, cw));
    } else {
      for (const g of recent) {
        recentRow(box, d.btn, s, ry, boxW, g, () => d.onWatch(g.replayKey));
        ry += 12;
      }
      // Why the Record can be smaller than the games listed: only ranked games (vs registered
      // opponents) count toward lifetime stats; bot / solo / too-short games are recorded for replay
      // but stay unranked. (See workers/leaderboard EloGame.getGameResult.)
      box.append(at(captionText('Unranked games don’t affect your record.', s), s, 10, ry + 1, cw));
    }
  }

  // A scroll container's scrollHeight comes from its CHILDREN, not its height — so the bottom margin
  // baked into bodyH is only honoured if something actually sits there. Drop a zero-size spacer at the
  // box bottom so a scrolled-to-end view leaves the last line room to breathe.
  box.append(at(el('div', { pointerEvents: 'none' }), s, 0, bodyH - 1, 1, 1));
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) d.onClose();
  });
  return overlay;
}

// One RECENT GAMES line: a W/L chip · map name · date, plus a right-anchored ▶ button (opens the
// replay viewer, onWatch → watchReplay) when the game has a recording. Columns anchor to boxW so the
// row fits both the 184u landscape box and the narrower 160u portrait box. The ▶ glyph (not the word
// "PLAY") keeps the button a compact square — its 36u-min text form collided with the date.
function recentRow(
  box: HTMLElement,
  btn: ShellBtn,
  s: number,
  y: number,
  boxW: number,
  g: { map: string; endUtc: number; won: boolean; replayKey: string; hasReplay: boolean },
  onWatch: () => void,
): void {
  const playW = buttonWidthU('▶', 9, s, 14);
  const playX = boxW - 8 - playW; // flush right (8u margin)
  const dateW = 52; // a fixed 10-char YYYY-MM-DD, never truncated
  const dateX = (g.hasReplay ? playX - 4 : boxW - 10) - dateW; // right-anchored, sitting left of ▶
  const mapX = 21,
    mapW = Math.max(20, dateX - 4 - mapX);
  box.append(at(rowText(g.won ? 'W' : 'L', s, g.won ? '#7ec97e' : '#e08a8a'), s, 10, y, 10));
  box.append(at(rowText(g.map, s), s, mapX, y, mapW)); // long map names ellipsise
  box.append(at(rowText(fmtDate(g.endUtc), s, COL_FG, '0.7'), s, dateX, y, dateW));
  if (g.hasReplay) {
    const play = stageButton(btn, s, '▶', playX, y - 1, playW, 9, onWatch);
    play.setAttribute('aria-label', 'Play replay');
    play.title = 'Play replay';
    box.append(play);
  }
}

function modalBox(
  title: string,
  w: number,
  h: number,
  onClose: () => void,
  onSignOut?: () => void,
): { overlay: HTMLElement; box: HTMLElement; s: number } {
  const overlay = el('div', {
    position: 'fixed',
    inset: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(0,0,0,0.45)',
    zIndex: '20',
  });
  overlay.dataset.list = '1';
  const s = formScale();
  // Frame chrome: the outer box is a fixed-size clipping FRAME (capped to the viewport so a short
  // LANDSCAPE phone never pushes content off-screen). The header bar is part of that frame — it does
  // NOT scroll. The body fills the frame and scrolls under the header; content keeps its design
  // coordinates because the body is inset:0 (same origin as the box).
  const box = el('div', {
    position: 'relative',
    width: `${w * s}px`,
    height: `${h * s}px`,
    maxHeight: 'calc(100dvh - 24px)',
    overflow: 'hidden',
    background: 'rgba(7,22,27,0.96)',
    border: `${Math.max(1, Math.round(s))}px solid #fff`,
    boxShadow: '0 6px 24px rgba(0,0,0,0.6)',
  });
  const body = el('div', {
    position: 'absolute',
    inset: '0',
    overflowY: 'auto',
    overflowX: 'hidden',
  });
  // The header (title + close ×) is drawn ON TOP of the body and stays put while the body scrolls.
  // Its side padding clears the close × (abspos, right) on BOTH sides so the title stays centred.
  // With a SIGN OUT button on the left, pad the title clear of it (and the × on the right) so the
  // centred title never overlaps either — the buttons are absolute (out of flow), the padding reserves
  // their lanes. Without sign-out the original symmetric 15u padding holds.
  const padLeft = onSignOut !== undefined ? 34 : 15;
  const bar = el('div', {
    position: 'absolute',
    top: '0',
    left: '0',
    right: '0',
    height: `${12 * s}px`,
    zIndex: '2',
    background: 'rgb(60, 96, 150)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: FONT,
    fontSize: `${Math.round(6 * s)}px`,
    fontWeight: '700',
    letterSpacing: '0.04em',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    padding: `0 ${15 * s}px 0 ${padLeft * s}px`,
    boxSizing: 'border-box',
  });
  bar.textContent = title;
  if (onSignOut !== undefined) {
    const so = el('button', {
      position: 'absolute',
      top: '0',
      left: '0',
      height: '100%',
      padding: `0 ${4 * s}px`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: 'none',
      background: 'transparent',
      color: '#fff',
      cursor: 'pointer',
      fontFamily: FONT,
      fontSize: `${Math.round(4.5 * s)}px`,
      fontWeight: '700',
      letterSpacing: '0.02em',
      whiteSpace: 'nowrap',
      lineHeight: '1',
    });
    so.textContent = 'SIGN OUT';
    so.setAttribute('aria-label', 'Sign out');
    so.title = 'Sign out';
    so.addEventListener('click', onSignOut);
    bar.append(so);
  }
  const x = el('button', {
    position: 'absolute',
    top: '0',
    right: '0',
    height: '100%',
    width: `${14 * s}px`,
    padding: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    background: 'transparent',
    color: '#fff',
    cursor: 'pointer',
    fontFamily: FONT,
    fontSize: `${Math.round(7 * s)}px`,
    fontWeight: '700',
    lineHeight: '1',
  });
  x.textContent = '✕';
  x.setAttribute('aria-label', 'Close');
  x.title = 'Close';
  x.addEventListener('click', onClose);
  bar.append(x);
  box.append(body, bar);
  overlay.append(box);
  // Content is appended to the BODY (the scroll region), not the frame.
  return { overlay, box: body, s };
}

function stageButton(
  btn: ShellBtn,
  s: number,
  label: string,
  x: number,
  y: number,
  w: number,
  h: number,
  on: () => void,
  en = true,
): HTMLElement {
  return at(btn(label, on, en, { w: w * s, h: h * s }), s, x, y);
}

// a single-line, ellipsizing text cell (normal weight) — the RECENT GAMES rows.
function rowText(text: string, s: number, color = COL_FG, opacity = '1'): HTMLElement {
  const e = el('div', {
    ...labelStyle(s),
    color,
    opacity,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: `${Math.round(5 * s)}px`,
  });
  e.textContent = text;
  return e;
}

// a small dimmed italic caption under the recent-games list — wraps (never truncates) so the
// one-liner survives the narrow portrait box; the reserved captionH covers a 2-line wrap.
function captionText(text: string, s: number): HTMLElement {
  const e = el('div', {
    fontFamily: FONT,
    fontSize: `${Math.round(4.5 * s)}px`,
    lineHeight: '1.1',
    color: COL_FG,
    opacity: '0.55',
    fontStyle: 'italic',
    textShadow: shadow,
  });
  e.textContent = text;
  return e;
}

// a centred notice (loading / empty / error) — wraps within the 164u body.
function centerText(text: string, s: number): HTMLElement {
  const e = el('div', { ...labelStyle(s), textAlign: 'center', opacity: '0.9' });
  e.textContent = text;
  return e;
}

function label(text: string, s: number, color = COL_FG): HTMLElement {
  const e = el('div', {
    ...labelStyle(s),
    color,
    fontWeight: '700',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  });
  e.textContent = text;
  return e;
}

// a "Label: value" detail line (the canvas GameForm static labels).
function detailLine(label: string, value: string, s: number): HTMLElement {
  const e = el('div', {
    ...labelStyle(s),
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: `${Math.round(5 * s)}px`,
  });
  const k = el('span', { opacity: '0.7' });
  k.textContent = `${label}: `;
  const v = el('span', { color: COL_FG });
  v.textContent = value;
  e.append(k, v);
  return e;
}

export function fmtHours(secs: number): string {
  if (secs <= 0) return '—';
  const h = secs / 3600;
  return h >= 10 ? `${Math.round(h)}h` : `${h.toFixed(1)}h`;
}
export function fmtDate(utc: number | null): string {
  if (utc === null || utc <= 0) return '—';
  try {
    return new Date(utc * 1000).toISOString().slice(0, 10);
  } catch {
    return '—';
  }
}

function labelStyle(s: number): Partial<CSSStyleDeclaration> {
  return {
    fontFamily: FONT,
    fontSize: `${Math.round(5.5 * s)}px`,
    color: COL_FG,
    textShadow: shadow,
  };
}
